/* Le magasin local : ce que le téléphone garde quand le serveur est loin.
 *
 * ── Pourquoi IndexedDB, et pas plus simple ──────────────────────────────────
 *
 * `localStorage` est synchrone : chaque écriture bloque le fil qui dessine.
 * Une balle enregistrée entre deux frappes, sur un parcours, coûterait une
 * saccade — précisément ce qu'on veut éviter. IndexedDB écrit de côté.
 *
 * Et il ne perd rien : le navigateur ne purge pas une base d'origine installée
 * comme il purge un cache. Un tour de dix-huit trous joué sans réseau tient
 * dans quelques kilo-octets, et il doit survivre à un téléphone qu'on éteint.
 *
 * ── Deux magasins, deux rôles ───────────────────────────────────────────────
 *
 *   * `file`   : les écritures en attente d'être portées au serveur, dans
 *                l'ordre où elles ont été faites ;
 *   * `etat`   : des instantanés d'écran, pour que l'application rouvre sur ce
 *                qu'on était en train de faire même sans réseau.
 *
 * ── Rien ici ne connaît le golf ─────────────────────────────────────────────
 *
 * Ce module ne sait ni ce qu'est un coup ni ce qu'est un trou : il range des
 * enregistrements. Ce qu'ils veulent dire est l'affaire de `file.ts`.
 */

const BASE = 'golf';
const VERSION = 1;
export const MAGASIN_FILE = 'file';
export const MAGASIN_ETAT = 'etat';

let ouverture: Promise<IDBDatabase | null> | null = null;

/** La base, ouverte une fois pour toutes. `null` si le navigateur la refuse —
 *  navigation privée, quota nul, réglage restrictif : on continue sans elle. */
function base(): Promise<IDBDatabase | null> {
    ouverture ??= new Promise<IDBDatabase | null>((resolve) => {
        let demande: IDBOpenDBRequest;
        try {
            demande = indexedDB.open(BASE, VERSION);
        } catch {
            resolve(null);
            return;
        }
        demande.onupgradeneeded = () => {
            const db = demande.result;
            if (!db.objectStoreNames.contains(MAGASIN_FILE)) {
                // `rang` est un compteur monotone : c'est lui qui donne l'ORDRE
                // de rejeu, et non la date — deux écritures dans la même
                // milliseconde arriveraient sinon dans un ordre indécis.
                db.createObjectStore(MAGASIN_FILE, { keyPath: 'rang', autoIncrement: true });
            }
            if (!db.objectStoreNames.contains(MAGASIN_ETAT)) {
                db.createObjectStore(MAGASIN_ETAT);
            }
        };
        demande.onsuccess = () => resolve(demande.result);
        demande.onerror = () => resolve(null);
        demande.onblocked = () => resolve(null);
    });
    return ouverture;
}

/** Exécute une transaction et rend son résultat. `null` si la base manque. */
async function transaction<T>(
    magasin: string,
    mode: IDBTransactionMode,
    action: (s: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
    const db = await base();
    if (!db) return null;
    return new Promise<T | null>((resolve) => {
        let t: IDBTransaction;
        try {
            t = db.transaction(magasin, mode);
        } catch {
            resolve(null);
            return;
        }
        const demande = action(t.objectStore(magasin));
        demande.onsuccess = () => resolve(demande.result as T);
        demande.onerror = () => resolve(null);
        t.onabort = () => resolve(null);
    });
}

export const lire = <T>(magasin: string, cle: IDBValidKey) =>
    transaction<T>(magasin, 'readonly', (s) => s.get(cle));

export const toutLire = <T>(magasin: string) =>
    transaction<T[]>(magasin, 'readonly', (s) => s.getAll()).then((r) => r ?? []);

export const ecrire = (magasin: string, valeur: unknown, cle?: IDBValidKey) =>
    transaction<IDBValidKey>(magasin, 'readwrite', (s) => (cle === undefined
        ? s.add(valeur)
        : s.put(valeur, cle)));

export const remplacer = (magasin: string, valeur: unknown, cle: IDBValidKey) =>
    transaction<IDBValidKey>(magasin, 'readwrite', (s) => s.put(valeur, cle));

export const effacer = (magasin: string, cle: IDBValidKey) =>
    transaction<undefined>(magasin, 'readwrite', (s) => s.delete(cle));

export const vider = (magasin: string) =>
    transaction<undefined>(magasin, 'readwrite', (s) => s.clear());

/** Un identifiant de la même forme que ceux du serveur, tiré ici.
 *
 *  C'est ce qui rend une écriture hors ligne ADRESSABLE : la série créée sans
 *  réseau porte déjà l'identifiant sous lequel le serveur l'enregistrera, et
 *  les balles qui suivent peuvent s'y rattacher tout de suite. Sans lui, il
 *  faudrait garder une table de correspondance et réécrire la file à chaque
 *  réponse — autant d'occasions de se tromper de coup. */
export function identifiant(): string {
    // `randomUUID` manque encore sur quelques navigateurs hors HTTPS ; le repli
    // tire les mêmes 122 bits d'aléa, avec la même forme.
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const o = crypto.getRandomValues(new Uint8Array(16));
    o[6] = (o[6]! & 0x0f) | 0x40;
    o[8] = (o[8]! & 0x3f) | 0x80;
    const h = [...o].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
